/**
 * Utility functions for interacting with device models
 * @namespace Util.Device
 */

import { confirm } from '@thermon-platforms/thermon-ui-core';
import { t } from '@lingui/core/macro';
import api, { parseErrors } from 'util/api';
import { isNumber } from 'lodash';

/**
  * @function getDeviceUrl
  * @memberof Util.Device
  * @param {object} device device modal from shallow models collection
  * @returns {string} compiled device detail url
  */
export function getDeviceUrl (device) {
  const root = '/devices';
  if (!device) return root;
  return device.path.reduce((accumulator, item) => {
    return `${accumulator}/${item.id}`;
  }, root) + `/${device.model.id}`;
}

/**
 * @function getAllCircuits
 * @memberof Util.Device
 * @param {object} device device model
 * @param {array} circuits (optional)
 * @returns {array}
 */
export function getAllCircuits(device, includeControllers = false, circuits = []) {
  if (device?.type == 'Controller' && includeControllers) {
    circuits.push(device);
  }
  if (device?.type == 'Circuit') {
    circuits.push(device);
  }
  if (device?.children?.length) device.children.forEach((child) => {
    getAllCircuits(child, includeControllers, circuits);
  });

  return circuits;
}

/**
 * @function getAllChildDevicesByType
 * @memberof Util.Device
 * @param {object|array} device initial device model or array of devices
 * @param {string} type device type to find
 * @param {array} devices (optional)
 * @returns {array}
 */
export function getAllChildDevicesByType(device, type, devices = []) {
  if (!device || !type) return devices;
  if (device.type == type) devices.push(device);
  if (Array.isArray(device)) {
    device.forEach((child) => getAllChildDevicesByType(child, type, devices));
  }
  if (device.children) {
    device.children.forEach((child) => getAllChildDevicesByType(child, type, devices));
  }
  return devices;
}

/**
 * @function lockOut
 * @memberof Util.Device
 * @param {object} device device model
 * @param {function} callback optional callback (most likely used to update state)
 */
export async function lockOut(device, callback = () => {}) {
  let item = getConfigItem(device, 'LockoutTagOut');
  if (!item) {
    item = {key: 'LockoutTagOut', value: 'off'};
    device.configuration.push(item);
  }
  if (item?.value == 'on') {
    const res = await api(`/devices/${device.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        type: device.type,
        configuration: [
          {
            key: 'LockoutTagOut',
            value: 'off'
          }
        ]
      })
    });
    item.value = 'off';
    callback(res);
    return;
  }
  confirm({
    header: `${t`Are you sure you want to lock out`} ${device.name}?`,
    message: t`Locking out this device does not guarantee safety and will only suppress alarms.`
  }).then(async () => {
    try {
      const res = await api(`/devices/${device.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          type: device.type,
          configuration: [
            {
              key: 'LockoutTagOut',
              value: 'on'
            }
          ]
        })
      });
      item.value = 'on';
      callback(res);
    } catch (err) {
      let errorBody;
      try {
        errorBody = await err.json();
      } catch (e) {
        errorBody = err?.message || 'An unknown error occurred';
      }
      callback({ok: false, errors: parseErrors(errorBody)});
    }
  }).catch(() => {
    callback('cancelled');
  });
}


/**
 * @function getConfigItem
 * @memberof Util.Device
 * @param {object} device device model
 * @param {string} name name of config item to return
 * @param {number} index index of the config item (default is 0)
 */
export function getConfigItem(device, key, index) {
  if (!device || !key) return undefined;
  let item;
  if (isNumber(index)) {
    item = device?.configuration?.find(c => c.key == key && c.deviceIndex == index);
  } else {
    item = device?.configuration?.find(c => c.key == key);
  }
  if (!item) return undefined;
  return item;
}

/**
 * Checks if the first 9 bits of a hex string equal decimal 3.
 * Example: "01d4008d00000000" => true
 * @function isDuo
 * @memberof Util.Misc
 * @param {string} value hex string payload
 * @returns {boolean}
 */
export function isDuo(value = '') {
  if (typeof value !== 'string') return false;

  const normalized = value.trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]+$/.test(normalized) || normalized.length < 4) return false;

  const firstTwoBytes = parseInt(normalized.slice(0, 4), 16);
  const firstNineBits = firstTwoBytes >> 7;
  return firstNineBits === 3;
}
